import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GetRequestForQuoteWithQuotesQuery } from '~/graphql/graphql';
import type { ProductGroup } from '~/pages/rfq-view/itemGroups';
import { SELLER_QUOTES_SECTION_ID } from '~/pages/rfq-view/sellerQuotesAnchor';
import { isClosedRequestForQuoteStatus } from '~/utils/requestForQuoteClosed';
import { ArchivedQuoteList } from './ArchivedQuoteList';
import { BuyerQuoteActions } from './BuyerQuoteActions';
import type { QuoteConversationDrafts } from './QuoteConversation';
import {
  negotiationContextOf,
  type QuoteNegotiationContexts,
} from './quoteNegotiation';
import type { RenderQuoteActions } from './SellerQuoteActionSlots';
import { SplitAwardComparisonGrid } from './SplitAwardComparisonGrid';
import { showsSplitAwardGrid } from './quoteDisplay';
import { VendorComparisonTable } from './VendorComparisonTable';

type Quote =
  GetRequestForQuoteWithQuotesQuery['quotesForRequestForQuote'][number];

type QuoteComparisonListProps = {
  requestForQuote: NonNullable<
    GetRequestForQuoteWithQuotesQuery['requestForQuote']
  >;
  quotes: Quote[];
  archivedQuotes: Quote[];
  groups: ProductGroup[];
  negotiationByRequestForQuoteId: QuoteNegotiationContexts;
};

export function QuoteComparisonList({
  requestForQuote,
  quotes,
  archivedQuotes,
  groups,
  negotiationByRequestForQuoteId,
}: Readonly<QuoteComparisonListProps>) {
  const { t } = useTranslation('rfq-view');
  const [conversationDrafts] = useState<QuoteConversationDrafts>(
    () => new Map()
  );

  const negotiationOf = (quote: Quote) =>
    negotiationContextOf(
      negotiationByRequestForQuoteId,
      quote.requestForQuote.id
    );

  const renderQuoteActions: RenderQuoteActions = (
    quote,
    onBuyerResponded,
    sellerResponse
  ) => (
    <BuyerQuoteActions
      quote={quote}
      negotiation={negotiationOf(quote)}
      conversationDrafts={conversationDrafts}
      onBuyerResponded={onBuyerResponded}
      sellerResponse={sellerResponse}
    />
  );

  return (
    <div className="flex flex-col gap-6">
      {quotes.length === 0 && archivedQuotes.length === 0 && (
        <p className="text-sm font-medium text-neut-sec">
          {t('rfq_view.no_quotes_yet')}
        </p>
      )}
      {quotes.length > 0 && (
        <div id={SELLER_QUOTES_SECTION_ID} className="scroll-mt-4">
          {showsSplitAwardGrid(requestForQuote, quotes) ? (
            <SplitAwardComparisonGrid
              quotes={quotes}
              groups={groups}
              isRequestForQuoteClosed={isClosedRequestForQuoteStatus(
                requestForQuote.status
              )}
              renderQuoteActions={renderQuoteActions}
              negotiationOf={negotiationOf}
            />
          ) : (
            <VendorComparisonTable
              quotes={quotes}
              groups={groups}
              renderQuoteActions={renderQuoteActions}
            />
          )}
        </div>
      )}
      {archivedQuotes.length > 0 && (
        <ArchivedQuoteList quotes={archivedQuotes} />
      )}
    </div>
  );
}
